import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useMemo } from "react";
import {
  allCoursesTags,
  eightCourseGrouping,
  courseDetails,
  ISection,
} from "../../../pages/v3/FAQPage";
import { FAQCourseTag as CourseTag } from "@backend-types";
import { SimpleCarousel } from "../SimpleCarousel";

const useStyles = makeStyles((theme) => ({
  rootSection: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "32px",
    transition: "box-shadow 500ms ease-in-out",
    [theme.breakpoints.down("md")]: {
      boxShadow: "0px 10px 20px 0px rgba(0, 0, 0, 0.1)",
      padding: "20px 12px",
      borderRadius: "8px",
    },
  },
  heading: {
    fontWeight: 700,
    letterSpacing: "unset",
    fontSize: "20px",
    lineHeight: "36px",
    marginBottom: "16px",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  },
  headingIcon: {
    color: "#007CBF",
  },
  accordionRoot: {
    border: "2px solid #D0D0D0",
    borderLeft: "8px solid #80449F",
    borderRadius: "16px !important",
    overflow: "hidden",
    boxSizing: "border-box",
    margin: "0px !important",
    transition: "border-color 0.2s ease-in-out",
    "& .MuiAccordionSummary-root": {
      minHeight: "unset !important",
    },
    "&:hover": {
      border: "2px solid #747474",
      borderLeft: "8px solid #80449F",
    },
    "&.Mui-expanded": {
      border: "2px solid #747474",
      borderLeft: "8px solid #80449F",
    },
    "&.Mui-focused": {
      border: "2px solid #747474",
      borderLeft: "8px solid #80449F",
    },
    [theme.breakpoints.down("md")]: {
      borderRadius: "8px !important",
    },
  },
  accordionSummary: {
    margin: "0px !important",
    padding: "11px 24px",
    "& .MuiAccordionSummary-content": {
      margin: "0px !important",
    },
    [theme.breakpoints.down("md")]: {
      padding: "16px",
    },
  },
  accordionDetails: {
    margin: "0px !important",
    padding: "24px",
    borderTop: `2px solid ${theme.palette.text.secondary}`,
    [theme.breakpoints.down("md")]: {
      padding: "16px",
    },
  },
  accordionHeading: {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "34px",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  accordionContent: {
    color: "#474747",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    textWrap: "pretty",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  expandChevron: {
    fontSize: "24px",
    color: theme.palette.text.secondary,
    padding: "8px",
    borderRadius: "50%",
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#DEDEDE",
    },
    "&:active": {
      backgroundColor: "#BFBFBF",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
      padding: "6px",
    },
  },
  tag: {
    height: "24px",
    borderRadius: "4px",
    borderWidth: "2px",
    padding: "4px",
  },
}));

const tagSetsEqual = (a: CourseTag[], b: CourseTag[]): boolean => {
  if (a.length !== b.length) return false;
  const aSet = new Set(a);
  return b.every((tag) => aSet.has(tag));
};
interface FAQSectionProps {
  section: ISection;
  singleCourseTag?: boolean;
  selectedTags: CourseTag[];
  onHover?: () => void;
}

export const FAQSection = ({
  section,
  singleCourseTag = false,
  selectedTags,
  onHover,
}: FAQSectionProps) => {
  const classes = useStyles();

  const sortedQuestions = useMemo(() => {
    return [...section.questions].sort((a, b) => {
      const getRank = (tags: CourseTag[]) => {
        if (tagSetsEqual(tags, allCoursesTags)) return 0;
        if (tagSetsEqual(tags, eightCourseGrouping)) return 1;
        if (tags.length === 1) return 2;
        return 3;
      };
      return getRank(a.tags) - getRank(b.tags);
    });
  }, [section.questions]);

  let lastTagGroup: CourseTag[] | null = null;

  return (
    <Box className={classes.rootSection} onMouseEnter={onHover}>
      <Typography variant="h3" component="h3" className={classes.heading} id={section.id}>
        <span className={classes.headingIcon}>{section.headingIcon}</span>{" "}
        {section.headingText}
      </Typography>

      <Stack gap="16px">
        {sortedQuestions.map((question, index) => {
          const isAllCourses = tagSetsEqual(question.tags, allCoursesTags);

          const showTags =
            !singleCourseTag &&
            !isAllCourses &&
            (lastTagGroup === null || !tagSetsEqual(question.tags, lastTagGroup));

          if (showTags) {
            lastTagGroup = question.tags;
          }

          const tagsToRender =
            selectedTags.length === 0
              ? question.tags
              : selectedTags.length === 1
              ? []
              : question.tags.filter((tag) => selectedTags.includes(tag));

          return (
            <React.Fragment key={`${question.question}-${index}`}>
              {showTags && tagsToRender.length > 0 && (
                <SimpleCarousel>
                  {tagsToRender.map((tag) => (
                    <Typography
                      key={`${question.question}-${index}-${tag}`}
                      whiteSpace="nowrap"
                      fontWeight={700}
                      fontSize="14px"
                      lineHeight="100%"
                      variant="body1"
                      component="span"
                      bgcolor="#D0D0D0"
                      borderColor="#D0D0D0"
                      color="#484848"
                      className={classes.tag}>
                      {courseDetails.get(tag)?.name ?? ""}
                    </Typography>
                  ))}
                </SimpleCarousel>
              )}

              <Accordion className={classes.accordionRoot}>
                <AccordionSummary
                  className={classes.accordionSummary}
                  expandIcon={
                    <i
                      className={`${classes.expandChevron} fa-solid fa-chevron-down`}></i>
                  }>
                  <Typography
                    variant="h4"
                    component="h4"
                    className={classes.accordionHeading}>
                    {question.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Typography variant="body2" className={classes.accordionContent}>
                    {question.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};
